import React from 'react';
import { reportRenderCrash } from '@config/errorReporting';

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

/**
 * Catches render crashes anywhere below it and reports them through the same choke point
 * as every other error class (sprint-15, story-073) — `LogRocket.captureException` +
 * `LogRocket.track('App Error', ...)`. No render-crash reporting existed anywhere in `web`
 * before this. A minimal fallback screen is intentional — this story is instrumentation, not
 * a redesign of the crash UI (see `docs/ux/story-073-design.md`).
 */
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    reportRenderCrash(error, info.componentStack ?? undefined);
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100vh',
            textAlign: 'center',
            padding: 24,
          }}
        >
          <h1>Something went wrong.</h1>
          <p>Please refresh the page. If this keeps happening, contact support.</p>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
